import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { candidateAPI } from "../services/api";
import RegistrationConfirmation from "./RegistrationConfirmation";
import "./CandidateRegistration.css";

const CandidateRegistration = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [subjects, setSubjects] = useState({});
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [isResending, setIsResending] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("NG"); // Default to Nigeria

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm();

  const watchPhoto = watch("photo");

  // Country to phone code mapping
  const countries = {
    NG: { name: "Nigeria", code: "+234" },
    US: { name: "United States", code: "+1" },
    GB: { name: "United Kingdom", code: "+44" },
    CA: { name: "Canada", code: "+1" },
    AU: { name: "Australia", code: "+61" },
    DE: { name: "Germany", code: "+49" },
    FR: { name: "France", code: "+33" },
    IN: { name: "India", code: "+91" },
    ZA: { name: "South Africa", code: "+27" },
    GH: { name: "Ghana", code: "+233" },
    KE: { name: "Kenya", code: "+254" },
    EG: { name: "Egypt", code: "+20" },
    MA: { name: "Morocco", code: "+212" },
    BR: { name: "Brazil", code: "+55" },
    CN: { name: "China", code: "+86" },
    JP: { name: "Japan", code: "+81" },
    UAE: { name: "United Arab Emirates", code: "+971" },
    SA: { name: "Saudi Arabia", code: "+966" }
  };

  const watchPhone = watch("phone");

  // Fetch subjects on component mount
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await candidateAPI.getSubjects();
        setSubjects(response.data);
      } catch (error) {
        console.error("Failed to fetch subjects:", error);
        toast.error("Failed to load subjects. Please try again.");
      }
    };

    fetchSubjects();
  }, []);

  // Set default country code only once on component mount
  useEffect(() => {
    setValue("phone", countries[selectedCountry].code, {
      shouldValidate: false,
      shouldDirty: false
    });
  }, []); // Empty dependency array to run only once

  // Handle photo preview
  React.useEffect(() => {
    if (watchPhoto && watchPhoto[0]) {
      const file = watchPhoto[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPhotoPreview(null);
    }
  }, [watchPhoto]);

  // Handle subject selection (limit to 2 subjects)
  const handleSubjectChange = (subjectId) => {
    const numericId = parseInt(subjectId, 10);
    setSelectedSubjects((prev) => {
      if (prev.includes(numericId)) {
        // Remove subject if already selected
        return prev.filter((id) => id !== numericId);
      } else {
        // Add subject only if less than 2 are selected
        if (prev.length < 2) {
          return [...prev, numericId];
        } else {
          toast.warning("You can only select a maximum of 2 subjects");
          return prev;
        }
      }
    });
  };

  // Handle country selection and update phone prefix
  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    const currentPhone = getValues("phone") || "";
    
    // Remove existing country code if present
    let phoneNumber = currentPhone;
    Object.values(countries).forEach(country => {
      if (phoneNumber.startsWith(country.code)) {
        phoneNumber = phoneNumber.substring(country.code.length);
        // Clean up any leading spaces or separators
        phoneNumber = phoneNumber.replace(/^[\s\-\.]+/, '');
      }
    });
    
    // Add new country code
    const newCountryCode = countries[countryCode].code;
    setValue("phone", phoneNumber ? `${newCountryCode}${phoneNumber}` : newCountryCode, {
      shouldValidate: false, // Don't trigger validation during programmatic update
      shouldDirty: false     // Don't mark field as dirty during programmatic update
    });
  };

  const handleResendEmail = async (email) => {
    setIsResending(true);

    try {
      await candidateAPI.resendRegistrationEmail(email);
      toast.success("Registration email has been resent successfully!");
    } catch (error) {
      console.error("Resend email error:", error);
      toast.error(error.message || "Failed to resend email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const onSubmit = async (data) => {
    // Validate subject selection
    if (selectedSubjects.length !== 2) {
      toast.error("Please select exactly 2 subjects");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();

      // Append all form fields
      Object.keys(data).forEach((key) => {
        if (key === "photo" && data[key][0]) {
          formData.append("photo", data[key][0]);
        } else if (key !== "photo") {
          formData.append(key, data[key]);
        }
      });

      // Append selected subjects
      if (selectedSubjects.length > 0) {
        formData.append("subjects", JSON.stringify(selectedSubjects));
      }
      
      // Append selected country
      formData.append("country", selectedCountry);

      const response = await candidateAPI.register(formData);
      setConfirmation({ candidate: response.data, emailSent: response.emailSent });
      toast.success(`Registration successful! Registration Number: ${response.data.regNo}`);
      reset();
      setPhotoPreview(null);
      setSelectedSubjects([]);
    } catch (error) {
      console.error("Registration error:", error);

      if (error.errors && Array.isArray(error.errors)) {
        error.errors.forEach((err) => {
          toast.error(err.message);
        });
      } else {
        toast.error(error.message || "Registration failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-container">
      {confirmation && <RegistrationConfirmation
        candidate={confirmation.candidate}
        emailSent={confirmation.emailSent}
        subjects={subjects}
        onClose={() => setConfirmation(null)}
      />}
      <div className="registration-card">
        <div className="card-header">
          <h2>Candidate Registration</h2>
          <p>Please fill in all required information to complete your registration</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="registration-form">
          {/* Personal Information Section */}
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstname">First Name *</label>
                <input
                  type="text"
                  id="firstname"
                  {...register("firstname", {
                    required: "First name is required",
                    minLength: { value: 1, message: "First name required" },
                    maxLength: { value: 30, message: "Max 30 characters" },
                  })}
                  className={errors.firstname ? "error" : ""}
                />
                {errors.firstname && <span className="error-message">{errors.firstname.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="middlename">Middle Name (Optional)</label>
                <input
                  type="text"
                  id="middlename"
                  {...register("middlename", {
                    maxLength: { value: 30, message: "Max 30 characters" },
                  })}
                  className={errors.middlename ? "error" : ""}
                />
                {errors.middlename && <span className="error-message">{errors.middlename.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lastname">Last Name *</label>
                <input
                  type="text"
                  id="lastname"
                  {...register("lastname", {
                    required: "Last name is required",
                    minLength: { value: 1, message: "Last name required" },
                    maxLength: { value: 30, message: "Max 30 characters" },
                  })}
                  className={errors.lastname ? "error" : ""}
                />
                {errors.lastname && <span className="error-message">{errors.lastname.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="school">School/Institution *</label>
                <input
                  type="text"
                  id="school"
                  {...register("school", {
                    required: "School/Institution is required",
                    minLength: { value: 2, message: "Min 2 characters" },
                    maxLength: { value: 100, message: "Max 100 characters" },
                  })}
                  className={errors.school ? "error" : ""}
                />
                {errors.school && <span className="error-message">{errors.school.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="dob">Date of Birth *</label>
                <input
                  type="date"
                  id="dob"
                  {...register("dob", {
                    required: "Date of birth is required",
                  })}
                  className={errors.dob ? "error" : ""}
                />
                {errors.dob && <span className="error-message">{errors.dob.message}</span>}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <h3>Contact Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="country">Country *</label>
                <select
                  id="country"
                  value={selectedCountry}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {Object.entries(countries).map(([code, country]) => (
                    <option key={code} value={code}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Please enter a valid email",
                    },
                  })}
                  className={errors.email ? "error" : ""}
                />
                {errors.email && <span className="error-message">{errors.email.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  placeholder={`Enter phone number (country code ${countries[selectedCountry].code} will be prefilled)`}
                  {...register("phone", {
                    required: "Phone number is required",
                    validate: (value) => {
                      // Check if it starts with the selected country code
                      const countryCode = countries[selectedCountry].code;
                      if (!value.startsWith(countryCode)) {
                        return `Phone number must start with ${countryCode}`;
                      }
                      
                      // Check minimum length (country code + at least 7 digits)
                      const phoneDigits = value.replace(/[^\d]/g, '');
                      if (phoneDigits.length < 10) {
                        return "Please enter a valid phone number";
                      }
                      
                      // Check maximum length
                      if (phoneDigits.length > 15) {
                        return "Phone number is too long";
                      }
                      
                      return true;
                    },
                  })}
                  className={errors.phone ? "error" : ""}
                />
                {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                <small className="help-text">Country code {countries[selectedCountry].code} is automatically added for {countries[selectedCountry].name}</small>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="form-section">
            <h3>Address Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="state">State *</label>
                <input
                  type="text"
                  id="state"
                  {...register("state", {
                    required: "State is required",
                    minLength: { value: 1, message: "State required" },
                    maxLength: { value: 50, message: "Max 50 characters" },
                  })}
                  className={errors.state ? "error" : ""}
                />
                {errors.state && <span className="error-message">{errors.state.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="lga">Local Government Area *</label>
                <input
                  type="text"
                  id="lga"
                  {...register("lga", {
                    required: "LGA is required",
                    minLength: { value: 1, message: "LGA required" },
                    maxLength: { value: 50, message: "Max 50 characters" },
                  })}
                  className={errors.lga ? "error" : ""}
                />
                {errors.lga && <span className="error-message">{errors.lga.message}</span>}
              </div>

              <div className="form-group" style={{ gridColumn: "span 2" }}>
                <label htmlFor="address">Full Address *</label>
                <input
                  type="text"
                  id="address"
                  {...register("address", {
                    required: "Address is required",
                    minLength: { value: 5, message: "Min 5 characters" },
                    maxLength: { value: 200, message: "Max 200 characters" },
                  })}
                  className={errors.address ? "error" : ""}
                />
                {errors.address && <span className="error-message">{errors.address.message}</span>}
              </div>
            </div>
          </div>

          {/* Subject Selection Section */}
          <div className="form-section">
            <h3>Subject Selection</h3>
            <div className="subjects-container">
              <label htmlFor="subjects">Select Subjects You Will Offer *</label>
              <div className="subjects-grid">
                {Object.entries(subjects).map(([id, subjectName]) => (
                  <div key={id} className="subject-checkbox">
                    <input
                      type="checkbox"
                      id={`subject-${id}`}
                      value={id}
                      checked={selectedSubjects.includes(parseInt(id, 10))}
                      onChange={() => handleSubjectChange(id)}
                      disabled={selectedSubjects.length >= 2 && !selectedSubjects.includes(parseInt(id, 10))}
                    />
                    <label htmlFor={`subject-${id}`}>{subjectName}</label>
                  </div>
                ))}
              </div>
              {selectedSubjects.length !== 2 && (
                <span className="error-message">
                  {selectedSubjects.length === 0 ? "Please select exactly 2 subjects" : `Please select ${2 - selectedSubjects.length} more subject${2 - selectedSubjects.length !== 1 ? "s" : ""}`}
                </span>
              )}
              <small className="help-text">You must select exactly 2 subjects for your examination ({selectedSubjects.length}/2 selected)</small>
            </div>
          </div>

          {/* Photo Upload Section */}
          <div className="form-section">
            <h3>Passport Photograph</h3>
            <div className="photo-upload-container">
              <div className="form-group">
                <label htmlFor="photo">Upload Passport Photo *</label>
                <input
                  type="file"
                  id="photo"
                  accept="image/*"
                  {...register("photo", {
                    required: "Passport photograph is required",
                  })}
                  className={errors.photo ? "error" : ""}
                />
                {errors.photo && <span className="error-message">{errors.photo.message}</span>}
                <small className="help-text">JPG, PNG, GIF. Max 5MB</small>
              </div>

              {photoPreview && (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Preview" />
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? "Registering..." : "Register Candidate"}
            </button>
          </div>
        </form>
      </div>

      {/* Resend Registration Email Section */}
      <div className="registration-card" style={{ marginTop: "2rem" }}>
        <div className="card-header">
          <h2>Already Registered?</h2>
          <p>If you've already registered and need to receive your registration email again, enter your email address below</p>
        </div>

        <div className="resend-email-section">
          <div className="form-group">
            <label htmlFor="resendEmail">Email Address *</label>
            <input type="email" id="resendEmail" placeholder="Enter your registered email address" onChange={(e) => setResendEmail(e.target.value)} className="resend-email-input" />
          </div>

          <button type="button" className="resend-btn" disabled={isResending || !resendEmail} onClick={() => handleResendEmail(resendEmail)}>
            {isResending ? "Sending..." : "Resend Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateRegistration;
